import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useGroupStore } from '@/stores/groupStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { GroupColorTheme, ResizeCorner } from '@/types';
import ResizeHandle from './ResizeHandle';
import GroupVisualFeedback from './GroupVisualFeedback';
import { getGroupTheme } from '@/config/groupThemes';
import { GroupToolbar } from '@/components/flow/GroupToolbar';
import { InverseScaleBox } from './parts/InverseScaleBox';
import '@/components/flow/styles/groupAnimations.css';
import { FolderTree, Film } from 'lucide-react';

const ALL_CORNERS: ResizeCorner[] = ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];

interface GroupNodeProps {
  id: string;
  data: {
    label?: string;
    color?: GroupColorTheme;
    groupType?: 'default' | 'storyboard';
    nodeWidth?: number;
    nodeHeight?: number;
  };
  width?: number;
  height?: number;
  selected?: boolean;
}

const GroupNode = ({ id, data, width, height }: GroupNodeProps) => {
  const setActiveGroup = useGroupStore(s => s.setActiveGroup);
  const setResizingGroup = useGroupStore(s => s.setResizingGroup);
  const resizeGroup = useGroupStore(s => s.resizeGroup);
  // P2: 移除响应式 zoom 订阅，改为使用时读取
  // 之前 useCanvasStore(s => s.zoom) 导致缩放时所有 GroupNode 重渲染
  // zoom 只在 resizeGroup 调用时使用，不需要实时订阅
  const resizingCorner = useGroupStore(s => s.resizingCorner);
  const activeGroupId = useGroupStore(s => s.activeGroupId);
  const groups = useGroupStore(s => s.groups);
  // P1-3: 改为订阅布尔值，避免每次选中变化都重渲染所有 GroupNode
  // 之前订阅整个 selectedNodeIds Set，任何选中变化都触发重渲染
  // 现在只有"是否多选"状态变化（0/1 → 2+ 或 2+ → 0/1）才触发重渲染
  const isMultiSelected = useSelectionStore(s => s.selectedNodeIds.size >= 2);
  const isBoxSelecting = useSelectionStore(s => s.isBoxSelecting);
  const multiSelected = isMultiSelected || isBoxSelecting;
  const isSelected = activeGroupId === id;
  const groupConfig = groups.get(id);
  const childCount = groupConfig?.childIds?.length || 0;

  const color = data.color || 'slate';
  const groupType = data.groupType || 'default';
  const theme = getGroupTheme(color);
  const label = childCount > 0 ? `组合 ${childCount} 个节点` : (data.label || '');

  const nodeWidth = data.nodeWidth || width || 400;
  const nodeHeight = data.nodeHeight || height || 300;

  const [isResizing, setIsResizing] = useState(false);
  const [hoveredCorner, setHoveredCorner] = useState<ResizeCorner | null>(null);
  const resizeStartRef = useRef<{ x: number; y: number } | null>(null);
  const isResizingRef = useRef(false);
  // P2: zoomRef 改为从 canvasStore 读取，不再依赖响应式订阅
  const zoomRef = useRef(useCanvasStore.getState().zoom);
  zoomRef.current = useCanvasStore.getState().zoom;

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isResizingRef.current) return;
    e.stopPropagation();
    useSelectionStore.getState().setSelectedNodeIds([id]);
    setActiveGroup(id);
  }, [id, setActiveGroup]);

  const handleResizeStart = useCallback((corner: ResizeCorner, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    isResizingRef.current = true;
    setResizingGroup(id, corner);
    setIsResizing(true);
    resizeStartRef.current = { x: e.clientX, y: e.clientY };
  }, [id, setResizingGroup]);

  useEffect(() => {
    if (!isResizing || !resizingCorner) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;
      resizeGroup(id, resizingCorner, deltaX, deltaY, zoomRef.current);
      resizeStartRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      isResizingRef.current = false;
      setResizingGroup(null, null);
      resizeStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizingCorner, id, resizeGroup, setResizingGroup]);

  const isStoryboard = groupType === 'storyboard';

  return (
    <div
      className={`group-node-wrapper ${isSelected ? 'selected' : ''} ${isResizing ? 'resizing' : ''}`}
      onClick={handleClick}
      style={{
        width: nodeWidth,
        height: nodeHeight,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: theme.bg,
          border: `1px solid ${isSelected ? 'rgba(255,255,255,0.25)' : theme.border}`,
          borderRadius: 8,
          boxShadow: isSelected
            ? `0 0 0 1px rgba(255,255,255,0.2), 0 8px 32px rgba(0,0,0,0.3)`
            : `0 4px 16px rgba(0,0,0,0.15)`,
          transition: isResizing ? 'none' : 'box-shadow 0.2s ease, border-color 0.2s ease',
        }}
      />

      <GroupVisualFeedback
        isExiting={false}
        exitProgress={0}
        direction="none"
        isResizing={isResizing}
        isSelected={isSelected}
        color={theme.border}
      />

      <InverseScaleBox
        minZoom={0.5}
        className="absolute left-0"
        style={{ top: '-25px', width: '210px', transformOrigin: 'left bottom', transform: 'translate(0px, -10px)', height: '25px' }}
        scaleOrigin="left bottom"
      >
        <div
          className="flex items-center gap-1.5 pointer-events-none"
          style={{ height: '25px', userSelect: 'none' }}
        >
          {isStoryboard ? (
            <Film size={16} className="text-white flex-shrink-0" />
          ) : (
            <FolderTree size={16} className="text-white flex-shrink-0" />
          )}
          <span className="text-[14px] font-medium text-white truncate">
            {label}
          </span>
        </div>
      </InverseScaleBox>

      {isSelected && !multiSelected && (
        <InverseScaleBox
          minZoom={0.5}
          fixedContent
          fitViewport
          className="absolute left-1/2 flex justify-center z-30 nodrag"
          style={{ bottom: '100%', paddingBottom: '36px', transform: 'translateX(-50%)', transformOrigin: 'center bottom', transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)' }}
          scaleOrigin="center bottom"
        >
          <GroupToolbar groupId={id} />
        </InverseScaleBox>
      )}

      {isSelected && ALL_CORNERS.map((corner) => (
        <ResizeHandle
          key={corner}
          corner={corner}
          onResizeStart={handleResizeStart}
          isHovered={hoveredCorner === corner}
          onHoverChange={setHoveredCorner}
        />
      ))}
    </div>
  );
};

export default memo(GroupNode, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.data?.nodeWidth === nextProps.data?.nodeWidth &&
    prevProps.data?.nodeHeight === nextProps.data?.nodeHeight &&
    prevProps.data?.color === nextProps.data?.color &&
    prevProps.data?.label === nextProps.data?.label &&
    prevProps.data?.groupType === nextProps.data?.groupType
  );
});
